import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { companyApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  CardActions,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LinkButton,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SegmentedTabs,
  ViewButton,
  useToast,
} from '@/components/ui';
import {
  blockedActionReason,
  isCandidateRestricted,
} from '@/features/applications/accountStanding';
import {
  BlockedAction,
  CandidateRestrictionBadge,
} from '@/features/applications/CandidateRestriction';
import { OfferDialog } from '@/features/offers/OfferDialog';
import { OfferLetterControls } from '@/features/offers/OfferLetterControls';
import { OFFER_LABELS, OFFER_TONES, respondByLine, stipendLine } from '@/features/offers/status';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { formatDate } from '@/utils/format';

const TABS = [
  { key: '', label: 'All' },
  { key: 'DRAFT', label: 'Drafts' },
  { key: 'SENT', label: 'Awaiting answer' },
  { key: 'ACCEPTED', label: 'Accepted' },
  { key: 'DECLINED', label: 'Declined' },
  { key: 'WITHDRAWN', label: 'Withdrawn' },
  { key: 'EXPIRED', label: 'Expired' },
];

const PAGE_SIZE = 20;

export function CompanyOffersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(null);
  // Editing, deleting a draft, withdrawing and changing the letter need
  // `offer.update`; this page only needs `offer.create`.
  const { can } = usePermissions();
  const canUpdate = can(PERMISSIONS.OFFER_UPDATE);
  // The listing's page needs only read access; without it the title stays
  // plain text rather than leading to "access denied".
  const canOpenInternship = can(PERMISSIONS.INTERNSHIP_READ);

  const params = { page, page_size: PAGE_SIZE, ...(status ? { statuses: status } : {}) };

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-offers', params],
    queryFn: () => companyApi.listOffers(params),
  });

  const { data: counts } = useQuery({
    queryKey: ['offer-counts'],
    queryFn: companyApi.getOfferCounts,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['company-offers'] });
    void queryClient.invalidateQueries({ queryKey: ['offer-counts'] });
  }

  const send = useMutation({
    mutationFn: (id) => companyApi.sendOffer(id),
    onSuccess: (offer) => {
      toast.success(`Offer sent to ${offer.candidate.full_name}`);
      invalidate();
    },
    onError: (sendError) => toast.error(errorMessage(sendError, 'Could not send the offer')),
  });

  const withdraw = useMutation({
    mutationFn: ({ id, reason: text }) => companyApi.withdrawOffer(id, text),
    onSuccess: () => {
      toast.success('Offer withdrawn');
      invalidate();
      setWithdrawing(null);
      setReason('');
    },
    onError: (withdrawError) => toast.error(errorMessage(withdrawError, 'Could not withdraw')),
  });

  const remove = useMutation({
    mutationFn: (id) => companyApi.deleteOffer(id),
    onSuccess: () => {
      toast.success('Draft deleted');
      invalidate();
      setDeleting(null);
    },
    onError: (deleteError) => toast.error(errorMessage(deleteError, 'Could not delete')),
  });

  const tabs = TABS.map((tab) => ({
    ...tab,
    count: counts && tab.key ? (counts[tab.key] ?? 0) : undefined,
  }));

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    // On a phone or tablet held upright the list card runs to the bottom of the
    // screen.
    <div className="flex flex-col portrait:min-h-[calc(100dvh-6rem)]">
      <PageHeader
        title="Offers"
        description="Draft the terms, send them, and see where each one stands."
      />

      <ListCard
        fill
        className="flex-1"
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            label="Offer status"
            tabs={tabs}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
          />
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading offers…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="document" size={22} />}
              title="No offers yet"
              description="Shortlist an applicant, then make an offer from their application."
              action={
                <LinkButton to={paths.company.applications} variant="secondary" size="sm">
                  Review applications
                </LinkButton>
              }
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((offer) => {
            const name = offer.candidate.full_name;
            const deadline = respondByLine(offer);
            // A sent offer stays with a suspended candidate, but nothing about
            // it can change until the account is reinstated.
            const blocked = blockedActionReason(offer);
            return (
              <BoxRow
                key={offer.id}
                muted={isCandidateRestricted(offer)}
                accent={blocked ? 'danger' : (OFFER_TONES[offer.status] ?? 'neutral')}
                leading={<BoxAvatar name={name} round />}
                title={name}
                to={paths.company.applicationDetail(offer.application_id)}
                badges={
                  <>
                    <Badge dot tone={OFFER_TONES[offer.status] ?? 'neutral'}>
                      {OFFER_LABELS[offer.status] ?? offer.status}
                    </Badge>
                    <CandidateRestrictionBadge row={offer} />
                  </>
                }
                subtitle={
                  <span className="text-ink-muted">
                    Offer for{' '}
                    {canOpenInternship ? (
                      <Link
                        to={paths.company.internshipDetail(offer.internship_id)}
                        onClick={(event) => event.stopPropagation()}
                        className="text-ink hover:text-brand-700 relative font-semibold underline-offset-2 hover:underline"
                      >
                        {offer.internship_title}
                      </Link>
                    ) : (
                      <span className="text-ink font-semibold">{offer.internship_title}</span>
                    )}
                    {offer.title && <> · {offer.title}</>}
                  </span>
                }
                facts={[
                  { icon: 'banknote', text: stipendLine(offer) },
                  { icon: 'calendar', text: `Starts ${formatDate(offer.starts_on)}` },
                  { icon: 'clockOutline', text: `Answer by ${formatDate(offer.respond_by)}` },
                  deadline && { icon: 'alert', text: deadline, tone: 'warning' },
                  offer.created_by && { icon: 'user', text: `Drafted by ${offer.created_by}` },
                ]}
                description={offer.withdrawal_reason && `Withdrawn: ${offer.withdrawal_reason}`}
                meta={
                  <span className="text-ink-muted text-xs">
                    {offer.sent_at
                      ? `Sent ${formatDate(offer.sent_at)}`
                      : `Created ${formatDate(offer.created_at)}`}
                  </span>
                }
                // A draft's main action is sending it; once sent, opening the
                // application is.
                actions={
                  <CardActions>
                    {canUpdate && offer.is_editable && (
                      <BlockedAction reason={blocked}>
                        <Button
                          variant="secondary"
                          size="sm"
                          title={`Edit the offer to ${name}`}
                          disabled={Boolean(blocked)}
                          onClick={() => setEditing(offer)}
                        >
                          <Icon name="pencil" size={14} />
                          Edit
                        </Button>
                      </BlockedAction>
                    )}
                    <ViewButton
                      to={paths.company.applicationDetail(offer.application_id)}
                      title={`Open ${name}'s application`}
                      label="View application"
                      icon="fileText"
                      variant={offer.is_editable ? 'secondary' : 'tintBrand'}
                    />
                    {offer.is_editable && (
                      <BlockedAction reason={blocked}>
                        <Button
                          variant="tintPositive"
                          size="sm"
                          title={`Send the offer to ${name}`}
                          disabled={Boolean(blocked)}
                          isLoading={send.isPending && send.variables === offer.id}
                          onClick={() => send.mutate(offer.id)}
                        >
                          <Icon name="send" size={14} />
                          Send offer
                        </Button>
                      </BlockedAction>
                    )}
                  </CardActions>
                }
                // Deleting a draft and withdrawing a sent offer both end it.
                corner={
                  <>
                    {canUpdate && offer.is_editable && (
                      <BlockedAction reason={blocked}>
                        <Button
                          variant="ghostDanger"
                          size="sm"
                          title={`Delete the draft for ${name}`}
                          disabled={Boolean(blocked)}
                          onClick={() => setDeleting(offer)}
                        >
                          <Icon name="trash" size={14} />
                          Delete draft
                        </Button>
                      </BlockedAction>
                    )}
                    {canUpdate && offer.is_open && !offer.is_editable && (
                      <BlockedAction reason={blocked}>
                        <Button
                          variant="ghostDanger"
                          size="sm"
                          title={`Withdraw the offer to ${name}`}
                          disabled={Boolean(blocked)}
                          onClick={() => setWithdrawing(offer)}
                        >
                          <Icon name="close" size={14} />
                          Withdraw
                        </Button>
                      </BlockedAction>
                    )}
                  </>
                }
              >
                {offer.response_message && (
                  <p className="text-ink-muted mt-2 text-sm italic">“{offer.response_message}”</p>
                )}
                <div className="mt-1">
                  <OfferLetterControls offer={offer} canChange={canUpdate && !blocked} />
                </div>
              </BoxRow>
            );
          })}
        </BoxList>
      </ListCard>

      {editing && <OfferDialog open onClose={() => setEditing(null)} offer={editing} />}

      <ConfirmDialog
        open={Boolean(deleting)}
        title={`Delete the draft for ${deleting?.candidate.full_name}?`}
        message="Nothing was sent, so nothing is lost but the text."
        confirmLabel="Delete"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />

      <Modal
        open={Boolean(withdrawing)}
        title={`Withdraw the offer to ${withdrawing?.candidate.full_name}?`}
        description="They may have turned other things down. The reason is shown to them."
        onClose={() => setWithdrawing(null)}
      >
        <div className="space-y-4">
          <Input
            label="Reason"
            required
            placeholder="The headcount for this role was cut."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setWithdrawing(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 4}
              isLoading={withdraw.isPending}
              onClick={() => withdraw.mutate({ id: withdrawing.id, reason: reason.trim() })}
            >
              Withdraw offer
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
